import { normalizeBoolean } from "@components/atoms/Filters/ButtonSection/Utils";
import { AddJewelryOptions, ProductCategory } from "@utils/constants";

/**
 * Jewelry type / linkage filters carried over from a previous builder step.
 * After Change on Review, setting search is the first screen of a new flow
 * and these must not be reused.
 *
 * `additional_filters` is not stripped here. When enterSecondFlow is false,
 * getProcessedQueryParams is supposed to add them for setting search.
 */
export const JEWELRY_SETTING_FIRST_FLOW_FILTER_KEYS = [
  "jewelry_styles",
  "stock_item_ids",
  "is_center_size_selected",
  "searchForRing",
  "searchForPendant",
  AddJewelryOptions.ENGAGEMENT_RING,
  AddJewelryOptions.PENDANT,
  AddJewelryOptions.EARRINGS,
  "pendant",
  "earring",
] as const;

/** Stone↔setting linkage only. User-selected jewelry types must stay searchable. */
export const JEWELRY_STONE_LINKAGE_KEYS = [
  "stock_item_ids",
  "is_center_size_selected",
] as const;

export function stripJewelryLinkageFromPayload<T extends Record<string, any>>(
  payload?: T | null,
  keys: readonly string[] = JEWELRY_SETTING_FIRST_FLOW_FILTER_KEYS
): T {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload as T;
  }

  const stripped: Record<string, any> = { ...payload };

  keys.forEach((key) => {
    delete stripped[key];
  });

  return stripped as T;
}

export function keepSettingSearchAdditionalFilters<
  T extends Record<string, any>
>(stripped: T, additionalFilters?: Record<string, any>): T {
  if (!additionalFilters || typeof additionalFilters !== "object") {
    return stripped;
  }

  const restored = { ...additionalFilters };
  delete restored.jewelry_styles;

  if (Object.keys(restored).length === 0) {
    return stripped;
  }

  return {
    ...stripped,
    additional_filters: restored,
  };
}

export function isJewelryProductType(productType?: string | null) {
  return (
    productType === ProductCategory.JEWELRY ||
    productType === ProductCategory.LABGROWN_JEWELRY
  );
}

/**
 * Change Stone on Review starts a new setting-first builder flow.
 * Browser back can restore the original stone→setting URL
 * (`enterSecondFlow=true&saved_search=...`), so jewelry screens must ignore
 * that leftover second-flow flag.
 */
export function getEffectiveEnterSecondFlow(
  enterSecondFlow: unknown,
  productType?: string | null,
  disableJewelrySettingStoneLinkage?: boolean
) {
  if (disableJewelrySettingStoneLinkage && isJewelryProductType(productType)) {
    return false;
  }
  return normalizeBoolean(enterSecondFlow);
}

export function toSettingSearchFirstFlowParams<T extends Record<string, any>>(
  params?: T | null
): T {
  const next: Record<string, any> = {
    ...(params || {}),
    enterSecondFlow: false,
  };
  delete next.saved_search;
  delete next.searchForPendant;
  delete next.searchForRing;
  return next as T;
}

export function needsSettingSearchFirstFlowParamCleanup(params?: any) {
  if (!params) return false;
  return (
    params.enterSecondFlow === true ||
    params.enterSecondFlow === "true" ||
    Boolean(params.saved_search)
  );
}
